import * as React from "react";

// ---------------------------------------------------------------------------
// Design-system Link abstraction.
//
// The design-system ships with a plain <a href> default. Consumers that run
// inside an SPA (e.g. React Router) should wrap their app in <LinkProvider>
// supplying a component that renders their router's link primitive. Every
// internal anchor the design-system emits goes through <SmartLink>, so SPA
// nav stays client-side and no full-page reloads occur.
// ---------------------------------------------------------------------------

export interface DsLinkProps extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  href: string;
  children?: React.ReactNode;
}

export type DsLinkComponent = React.ComponentType<DsLinkProps>;

const DefaultLink: DsLinkComponent = ({ href, children, ...rest }) => (
  <a href={href} {...rest}>
    {children}
  </a>
);

const LinkContext = React.createContext<DsLinkComponent>(DefaultLink);

export interface LinkProviderProps {
  component: DsLinkComponent;
  children: React.ReactNode;
}

export function LinkProvider({ component, children }: LinkProviderProps) {
  return <LinkContext.Provider value={component}>{children}</LinkContext.Provider>;
}

export function useLinkComponent(): DsLinkComponent {
  return React.useContext(LinkContext);
}

function isExternalHref(href: string): boolean {
  return /^(https?:|mailto:|tel:|#)/.test(href);
}

export function SmartLink({ href, children, ...rest }: DsLinkProps) {
  const linkComponent = useLinkComponent();
  if (isExternalHref(href)) {
    return (
      <a href={href} {...rest}>
        {children}
      </a>
    );
  }
  return React.createElement(linkComponent, { href, ...rest }, children);
}
